import { Path } from './translationUtils';
import styles from './ValidationErrors.module.scss';

import {
    isValidationMessage,
    ValidationSeverity,
} from './templates/validators';

const TOKEN_ENTRIES = [
    { token: /^\.?pages\.(\d)/, name: 'Page {0}' },
    { token: /^\.?body\.text\.([^.]+)/, name: 'Body ({0})' },
    { token: /^\.?header.text\.([^.]+)/, name: 'Header ({0})' },
    { token: /^\.?button\.text\.([^.]+)/, name: 'Button' },
    { token: /^\.?popTexts\.(\d)/, name: 'PopText {0}' },
    { token: /^\.?rows\.(\d)\.text/, name: 'Row {0}' },
    { token: /^\.?totalMaxLength\.([^.]+)/, name: 'Header and Body ({0})' },
    { token: /^\.?poll\.alternatives\.(\d)/, name: 'Poll Option {0}' },
];

function getTokenMatch(text: string) {
    for (const tokenEntry of TOKEN_ENTRIES) {
        const match = tokenEntry.token.exec(text);
        if (match) {
            return { tokenEntry, match };
        }
    }
    return null;
}
/**
 * Converts an error path to a human readable name
 */
function pathToFieldName(path: Path): string {
    let pathStr = path
        .map((part) => {
            // Convert 0-based index to 1-based
            if (typeof part === 'number') {
                return part + 1;
            }
            return part;
        })
        .join('.');
    const fieldName: string[] = [];

    while (pathStr) {
        const tokenMatch = getTokenMatch(pathStr);
        if (!tokenMatch) {
            // Couldn't convert the whole string to a display name,
            // bailing out with just path
            return path.join('.');
        }

        pathStr = pathStr.replace(tokenMatch.tokenEntry.token, '');

        let fieldNamePart = tokenMatch.tokenEntry.name;
        tokenMatch.match.slice(1).forEach((captureGroup, i) => {
            fieldNamePart = fieldNamePart.replace(`{${i}}`, captureGroup);
        });
        fieldName.push(fieldNamePart);
    }

    return fieldName.join(' ⟶ ');
}

/**
 * Flattens a nested object to an array of objects, with the object value path as key and the leaf error message string as value
 */
function errorsToArray(
    errors: unknown | Record<string, unknown>,
): Array<{ path: Path; message: string; severity: ValidationSeverity }> {
    const messages: Array<{
        path: Path;
        message: string;
        severity: ValidationSeverity;
    }> = [];

    if (typeof errors === 'string') {
        return [{ path: [], message: errors, severity: 'error' }];
    } else if (isValidationMessage(errors)) {
        return [
            { path: [], message: errors.message, severity: errors.severity },
        ];
    } else if (typeof errors === 'object' && errors !== null) {
        for (const [key, value] of Object.entries(errors)) {
            // Ensure numeric keys are parsed to numbers
            const parsedKey = /^\d+$/.test(key) ? Number(key) : key;
            for (const error of errorsToArray(value)) {
                messages.push({
                    path: [parsedKey, ...error.path],
                    message: error.message,
                    severity: error.severity,
                });
            }
        }
    }

    return messages;
}

export interface ValidationErrorsProps {
    errors: Record<string, unknown>;
}

export function ValidationErrors({ errors }: ValidationErrorsProps) {
    const flatErrors = errorsToArray(errors);
    const errorItems = flatErrors.filter((e) => e.severity === 'error');
    const warningItems = flatErrors.filter((e) => e.severity === 'warning');

    return (
        <div>
            {flatErrors.length > 0 ? (
                <>
                    {errorItems.map((item) => (
                        <p key={item.path.toString()} className={styles.error}>
                            ❌ {pathToFieldName(item.path)}: {item.message}
                        </p>
                    ))}
                    {warningItems.map((item) => (
                        <p
                            key={item.path.toString()}
                            className={styles.warning}
                        >
                            ⚠️ {pathToFieldName(item.path)}: {item.message}
                        </p>
                    ))}
                </>
            ) : (
                <p>✅ No Errors Found</p>
            )}
        </div>
    );
}
